import { CRITERIOS_DE_LEAD, ROTULO_DO_CRITERIO, type ConexaoMeta, type CriterioDeLead } from "@base/contracts";
import {
  Button,
  CabecalhoDePagina,
  CampoDeTexto,
  EstadoCarregando,
  EstadoDeErro,
  cn,
  toast,
} from "@base/ui";
import { ArrowLeft, Check, KeyRound, LoaderCircle, Megaphone, RefreshCw, Search, ShieldCheck } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";
import { Link } from "../../../shared/navegacao/navegacao";
import { formatar } from "../../dashboard";
import {
  useConexaoMeta,
  useContasDeAnuncio,
  useCredencialMeta,
  useDesconectarMeta,
  useSalvarConexaoMeta,
  useSincronizarMeta,
} from "../application/conexoes.contexto";
import { ResumoDoAcessoDaMonte } from "./acesso-da-monte";

export const CAMINHO_DO_META = "/configuracoes/conexoes/meta";

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();

/**
 * Configuracao do Meta Ads da clinica: o estado do acesso da Monte (trocado em
 * Configuracoes da Monte) e as contas de anuncio desta clinica. Alimenta o
 * investimento e os leads do gerenciador no Dashboard.
 */
export function PaginaDoMeta({
  clienteId,
  cliente,
  equipeMonte,
}: {
  readonly clienteId: string;
  readonly cliente: string;
  readonly equipeMonte: boolean;
}) {
  const credencial = useCredencialMeta();
  const conexao = useConexaoMeta(clienteId);
  const configurada = credencial.data?.configurada ?? false;

  return (
    <div className="flex flex-col gap-secao">
      <div className="flex flex-col gap-4">
        <Link
          para="/configuracoes/conexoes"
          className="inline-flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Conexões
        </Link>
        <CabecalhoDePagina
          rotulo={`${cliente} · Conexões`}
          titulo="Meta Ads"
          descricao="Investimento e leads do gerenciador de anúncios (Facebook e Instagram) desta clínica."
        />
      </div>

      {credencial.isPending || conexao.isPending ? (
        <EstadoCarregando rotulo="Carregando o Meta Ads" />
      ) : credencial.isError || conexao.isError ? (
        <EstadoDeErro mensagem={mensagemDoErro(credencial.error ?? conexao.error)} />
      ) : (
        <>
          <ResumoDoAcessoDaMonte
            plataforma="Meta"
            configurada={configurada}
            nome={credencial.data.nomeNoMeta}
            equipeMonte={equipeMonte}
          />
          {configurada ? (
            <ContasDaClinica
              key={`${conexao.data.contas.join(",")}|${conexao.data.criterios.join(",")}`}
              clienteId={clienteId}
              conexao={conexao.data}
            />
          ) : null}
        </>
      )}
    </div>
  );
}

function ContasDaClinica({ clienteId, conexao }: { readonly clienteId: string; readonly conexao: ConexaoMeta }) {
  const contas = useContasDeAnuncio(clienteId, true);
  const salvar = useSalvarConexaoMeta(clienteId);
  const sincronizar = useSincronizarMeta(clienteId);
  const desconectar = useDesconectarMeta(clienteId);
  const [escolhidas, setEscolhidas] = useState<ReadonlySet<string>>(() => new Set(conexao.contas));
  const [criterios, setCriterios] = useState<ReadonlySet<CriterioDeLead>>(
    () => new Set(conexao.criterios.length > 0 ? conexao.criterios : CRITERIOS_DE_LEAD),
  );
  const [busca, setBusca] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const conectada = conexao.situacao !== "nao-conectada";
  const sincronizando = conexao.situacao === "sincronizando" || sincronizar.isPending;

  const visiveis = useMemo(() => {
    const termo = normalizar(busca.trim());
    return (contas.data ?? []).filter(
      (conta) => termo === "" || normalizar(conta.nome).includes(termo) || conta.id.includes(termo),
    );
  }, [contas.data, busca]);

  const alterado =
    escolhidas.size !== conexao.contas.length ||
    conexao.contas.some((conta) => !escolhidas.has(conta)) ||
    criterios.size !== conexao.criterios.length ||
    conexao.criterios.some((criterio) => !criterios.has(criterio));

  function alternar<T>(conjunto: ReadonlySet<T>, item: T): Set<T> {
    const proximo = new Set(conjunto);
    if (proximo.has(item)) proximo.delete(item);
    else proximo.add(item);
    return proximo;
  }

  return (
    <section className="flex flex-col gap-bloco">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-normal tracking-tight">Contas de anúncio desta clínica</h2>
          <p className="text-sm text-muted-foreground">
            O investimento e os leads somam todas as contas marcadas. Marque só as desta clínica.
          </p>
        </div>
        {conectada ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            {conexao.ultimaSincronizacaoEm ? `Sincronizado em ${formatar.momento(conexao.ultimaSincronizacaoEm)}` : null}
            <Button variant="outline" disabled={sincronizando} onClick={() => sincronizar.mutate()}>
              <RefreshCw className={cn(sincronizando && "animate-spin")} aria-hidden="true" />
              {sincronizando ? "Sincronizando…" : "Sincronizar agora"}
            </Button>
          </div>
        ) : null}
      </div>

      {conexao.ultimoErro ? (
        <p role="alert" className="rounded-2xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {conexao.ultimoErro}
        </p>
      ) : null}

      <div className="grid items-start gap-bloco lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="overflow-hidden rounded-3xl bg-card shadow-sm ring-1 ring-border/70">
          <label className="flex h-12 items-center gap-2 border-b px-5">
            <Search className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <input
              value={busca}
              onChange={(evento) => setBusca(evento.target.value)}
              placeholder="Buscar conta pelo nome ou ID"
              aria-label="Buscar conta de anúncio"
              className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
            <span className="shrink-0 text-xs text-muted-foreground">{escolhidas.size} marcada(s)</span>
          </label>
          {contas.isPending ? (
            <p className="px-5 py-6 text-sm text-muted-foreground">Buscando as contas no Meta…</p>
          ) : contas.isError ? (
            <p role="alert" className="px-5 py-6 text-sm text-destructive">
              {mensagemDoErro(contas.error)}
            </p>
          ) : (
            <ul className="max-h-[26rem] overflow-y-auto p-2" aria-label="Contas de anúncio">
              {visiveis.map((conta) => {
                const marcada = escolhidas.has(conta.id);
                return (
                  <li key={conta.id}>
                    <label
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors hover:bg-accent",
                        marcada && "bg-accent/60",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={marcada}
                        onChange={() => setEscolhidas((atual) => alternar(atual, conta.id))}
                        className="peer sr-only"
                      />
                      <span
                        aria-hidden="true"
                        className={cn(
                          "grid size-4 shrink-0 place-items-center rounded border peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40",
                          marcada ? "border-primary bg-primary text-primary-foreground" : "border-input bg-background",
                        )}
                      >
                        {marcada ? <Check className="size-3" /> : null}
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate font-medium">{conta.nome}</span>
                        <span className="text-xs text-muted-foreground tabular-nums">act_{conta.id}</span>
                      </span>
                      {!conta.ativa ? (
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                          Inativa
                        </span>
                      ) : null}
                    </label>
                  </li>
                );
              })}
              {visiveis.length === 0 ? (
                <li className="px-3 py-6 text-center text-sm text-muted-foreground">Nenhuma conta encontrada.</li>
              ) : null}
            </ul>
          )}
        </div>

        <aside className="flex flex-col gap-4 rounded-3xl bg-secondary p-6 text-secondary-foreground">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Megaphone className="size-4 text-ice" aria-hidden="true" />
            O que conta como lead
          </p>
          <div className="flex flex-col gap-2">
            {CRITERIOS_DE_LEAD.map((criterio) => {
              const marcado = criterios.has(criterio);
              return (
                <label key={criterio} className="flex cursor-pointer items-start gap-3 rounded-xl bg-white/5 px-3 py-2.5 text-sm">
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() => setCriterios((atual) => alternar(atual, criterio))}
                    className="mt-0.5 accent-[var(--color-ice)]"
                  />
                  {ROTULO_DO_CRITERIO[criterio]}
                </label>
              );
            })}
          </div>
          <p className="text-xs leading-relaxed opacity-75">
            Leads do gerenciador = soma do que estiver marcado, no dia em que aconteceu.
          </p>
        </aside>
      </div>

      {salvar.isError ? (
        <p role="alert" className="text-sm text-destructive">
          {mensagemDoErro(salvar.error)}
        </p>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        {conectada ? (
          confirmando ? (
            <span className="flex flex-wrap items-center gap-2 text-sm">
              Desconectar? Investimento e leads do gerenciador voltam a ficar em branco.
              <Button size="sm" variant="destructive" onClick={() => desconectar.mutate(undefined, { onSuccess: () => setConfirmando(false) })}>
                Desconectar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmando(false)}>
                Cancelar
              </Button>
            </span>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmando(true)}
              className="text-sm text-muted-foreground underline-offset-4 hover:text-destructive hover:underline"
            >
              Desconectar o Meta desta clínica
            </button>
          )
        ) : (
          <span />
        )}
        <Button
          disabled={salvar.isPending || escolhidas.size === 0 || criterios.size === 0 || (conectada && !alterado)}
          onClick={() =>
            salvar.mutate(
              { contas: [...escolhidas], criterios: [...criterios] },
              { onSuccess: () => toast.success("Meta Ads conectado. Buscando o histórico…") },
            )
          }
        >
          {salvar.isPending ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
          {conectada ? "Salvar alterações" : "Conectar contas"}
        </Button>
      </div>
    </section>
  );
}
